import { useNotarizationState } from "./useNotarizationState";
import useGetCurrentUserDocument from "./useGetCurrentUserDocument";
import { useGetAnswerQuery } from "../api/answers";
import { PaymentStatus } from "../../../api/src/types";
import { ProductType } from "../../../api/src/types";
import { skipToken } from "@reduxjs/toolkit/query";
import config from "../config";
import { useDocumentGenState } from "./useDocumentGenState";

export const useQuestionnaireSectionState = () => {
  const {
    user,
    document,
    isLoading: isDocumentLoading,
    error: userDocumentError,
  } = useGetCurrentUserDocument();
  const {
    isNotarizationComplete,
    isNotarizationInProgress,
    isOnlineNotaryEnabled,
    isLoading: isNotarizationLoading,
    error: notarizationError,
  } = useNotarizationState();

  const {
    userAMadeChanges,
    userBMadeChanges,
    needsRegeneration,
    isLoading: isRegenerationLoading,
    error: isRegenerationError,
  } = useDocumentGenState();

  const {
    data: weddingDateAnswer,
    isLoading: isWeddingDateAnswerLoading,
    isError: isWeddingDateAnswerError,
  } = useGetAnswerQuery(
    user?.id && document?.id
      ? {
          questionId: config.weddingDateQuestionId,
          userId: user.id,
          documentId: document.id,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const weddingDate =
    !isWeddingDateAnswerLoading &&
    !isWeddingDateAnswerError &&
    weddingDateAnswer
      ? weddingDateAnswer[0]?.valueTyped
      : null;

  const isDocumentGenerated = !!document?.lastGeneratedAt;

  const isDocumentReviewed = !!document?.reviewedAt;

  const isDocumentFinalized = !!document?.finalizedAt;

  // Find if any answers are modified after the document section was created
  const userModId = userAMadeChanges
    ? document?.userAId
    : userBMadeChanges
      ? document?.userBId
      : undefined;

  //Check if document is edited
  const isDocumentInEditor = !!(
    isDocumentGenerated &&
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    (document.attorneyAId || document.attorneyBId) &&
    !document.finalizedAt
  );

  //Check if current user will hire attorney and has representation purchase
  const currrentUserWillHireAttorney = !!(document?.userAId === user?.id
    ? document?.userAWillHireAttorney
    : document?.userBWillHireAttorney);

  // Ensure purchaseItems is an array before using array methods
  const purchaseItems = Array.isArray(document?.purchaseItems)
    ? document.purchaseItems
    : [];

  const currentUserRepresentationPurchase = !!purchaseItems.some(
    (item) =>
      item.product.productType === ProductType.REPRESENTATION &&
      item.paymentStatus === PaymentStatus.SUCCESSFUL &&
      item.userId === user?.id,
  );

  const isPrenupPurchased = !!purchaseItems.some(
    (item) =>
      item.product.productType === ProductType.PRENUP &&
      item.paymentStatus === PaymentStatus.SUCCESSFUL &&
      item.deletedAt === null,
  );

  const isDocumentInProof = isNotarizationInProgress && isOnlineNotaryEnabled;

  const isQuestionnaireBlocked =
    isDocumentInEditor ||
    isDocumentInProof ||
    isNotarizationComplete ||
    isDocumentReviewed ||
    isDocumentFinalized;

  return {
    isDocumentGenerated,
    hasAnswersModifiedAfterDocumentGeneration: needsRegeneration,
    userModId,
    isPrenupPurchased,
    isDocumentInEditor,
    isDocumentReviewed,
    isDocumentFinalized,
    isDocumentInProof,
    isNotarizationComplete,
    currrentUserWillHireAttorney,
    currentUserRepresentationPurchase,
    isQuestionnaireBlocked,
    weddingDate,
    weddingDateAnswer,
    isLoading:
      isDocumentLoading || isNotarizationLoading || isRegenerationLoading,
    error: userDocumentError ?? notarizationError ?? isRegenerationError,
  };
};
